import React, { Dispatch, SetStateAction, use, useEffect, useMemo, useState } from "react";
import AuthService from "../services/AuthService";
import { UserLogged } from "../models/UserLogged";
import { AccessProfileService } from "../services/Access/AccessProfile/AccessProfileService";
import { ToastSettings } from "../utils/ToastSettings";
import { ProfileService } from "../services/Profile/ProfileService";

interface ContextProps {
  userLogged: UserLogged | null;
  setUserLogged: Dispatch<SetStateAction<UserLogged | null>>;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  accessToken: string;
  tokenLacqua: string;
  setTokenLacqua: Dispatch<SetStateAction<string>>;
  login: (data: { username: string; password: string }) => Promise<any>;
  authorization: (data: any) => Promise<any>;
  logout: () => Promise<{ success: boolean; error: any }>;
  onInvalid: (property?: any | undefined) => string;
  isLoadingLogged: boolean;
  isLoadingPassword: boolean;
  isMainNavbar: boolean;
  origin: string | null;
  setOrigin: Dispatch<SetStateAction<string>>;
  setIsMainNavbar: Dispatch<SetStateAction<boolean>>;
  isDevelopment: boolean;
  forgot: (username: string) => Promise<boolean>;
  reset: (data: any) => Promise<boolean>;
  profileImageUrl: string | null;
  refreshProfileImage: () => Promise<void>;
  refreshProfileImageLocal: (imagePath: string) => void;
}

export const AuthContext = React.createContext<ContextProps>(
  {} as ContextProps
);

export const AuthProvider = (props: any) => {
  const [userLogged, setUserLogged] = useState<UserLogged | null>(null);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingLogged, setIsLoadingLogged] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string>("");
  const [origin, setOrigin] = useState<string>("");
  const [tokenLacqua, setTokenLacqua] = useState<string>("");
  const [isLoadingPassword, setIsLoadingPassword] = useState<boolean>(false);
  const [isMainNavbar, setIsMainNavbar] = useState<boolean>(true);
  const profileService = new ProfileService();
  const apiUrl = process.env.REACT_APP_BEVIAJUDA_API ?? "";

  const profileImageUrl = useMemo(() => {
    const imagePath = userLogged?.user_profile_image;
    if (!imagePath) return null;
    const base = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
    const path = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
    return `${base}${path}`;
  }, [userLogged?.user_profile_image, apiUrl]);

  useEffect(() => {
    const disposer = () => {
      const userLoggedStorage = localStorage.getItem(`@beviAjudaGui:user`);
      setUserLogged(!!userLoggedStorage ? JSON.parse(userLoggedStorage) : null);
    };
    disposer();
  }, []);

  useEffect(() => {
    if (userLogged) {
      localStorage.setItem("@beviAjudaGui:user", JSON.stringify(userLogged));
    }
  }, [userLogged]);

  useEffect(() => {
    const disposer = () => {
      let token = "";
      token = localStorage.getItem("@beviAjudaGui:auth") || "";
      token = token && token !== "undefined" ? JSON.parse(token) : "";
      setAccessToken(token);
    };
    disposer();
  }, []);

  useEffect(() => {
    const disposer = () => {
      let token = "";
      token = localStorage.getItem("@beviAjudaGui:tokenLacqua") || "";
      token = token && token !== "undefined" ? token : "";
      setTokenLacqua(token);
    };
    disposer();
  }, []);

  useEffect(() => {
    if (origin) {
      localStorage.setItem('@beviAjudaGui:origin', `https://${origin}`);
    }
  }, [origin]);

  const login = async (data: { username: string; password: string }) => {
    try {
      setIsLoading(true);

      const [_Response, _Error, _Code] = await new AuthService().login(data);

      if (!!_Error || _Code === 401 || !_Response) {
        setIsLoading(false);
        setError(
          _Response ||
          _Error ||
          "Os dados informados não conferem, por favor verique os dados e tente novamente."
        );
        return false;
      }

      localStorage.setItem(
        `@beviAjudaGui:auth`,
        JSON.stringify(_Response.access_token)
      );

      const [_ResponseProfile, _ErrorProfile] =
        await new AccessProfileService().menu();

      let userRes: UserLogged = {
        id: _Response?.user?.id,
        name: _Response?.user?.name,
        partner_id: _Response?.user?.partner_id,
        partner_code: _Response?.user?.partner_code,
        username: _Response?.user?.username,
        role: _Response?.user?.role,
        access_profile: _Response?.user?.access_profile,
        access_profile_id: _Response?.user?.access_profile?.id,
        user_type: _Response?.user?.user_type,
        menu: _ResponseProfile?.data?.items || [],
        user_profile_image: _Response?.user?.user_profile_image || '',
        dsim_widget: _Response?.user?.dsim_widget || null,
      };

      setUserLogged((prev) => ({ ...prev, ...userRes }));

      setIsLoading(false);

      return { success: true, user: userRes };
    } catch (err) {
      return { success: false, error: err };
    }
  };

  const authorization = async (data: any) => {
    try {
      setIsLoading(true);

      const [_Response, _Error, _Code] =
        await new AuthService().authorizationSSO(data);

      if (!!_Error || _Code === 401 || !_Response) {
        setIsLoading(false);
        setError(
          _Response ||
          _Error ||
          "Os dados informados não conferem, por favor verique os dados e tente novamente."
        );
        return false;
      }

      localStorage.setItem(
        `@beviAjudaGui:auth`,
        JSON.stringify(_Response.access_token)
      );

      const [_ResponseProfile, _ErrorProfile] =
        await new AccessProfileService().menu();

      let userRes: UserLogged = {
        id: _Response?.user?.id,
        name: _Response?.user?.name,
        partner_id: _Response?.user?.partner_id,
        partner_code: _Response?.user?.partner_code,
        username: _Response?.user?.username,
        role: _Response?.user?.role,
        access_profile: _Response?.user?.access_profile,
        access_profile_id: _Response?.user?.access_profile?.id,
        user_type: _Response?.user?.user_type,
        menu: _ResponseProfile?.data?.items || [],
        user_profile_image: _Response?.user?.user_profile_image || '',
        dsim_widget: _Response?.user?.dsim_widget || null,
      };

      setUserLogged((prev) => ({ ...prev, ...userRes }));

      setIsLoading(false);

      return { success: true, user: userRes };
    } catch (err) {
      return { success: false, error: err };
    }
  };

  const forgot = async (data: any) => {
    setIsLoadingPassword(true);
    try {
      const [_Response, _Error, _Code, _Errors] =
        await new AuthService().forgot(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => { },
          _Errors
        );
        setIsLoadingPassword(false);
        return false;
      }

      setError("");
      setIsLoadingPassword(false);
      return true;
    } catch (e) {
      setError("Houve um erro ao enviar e-mail.");
      setIsLoadingPassword(false);
      return false;
    }
  };

  const reset = async (data: any) => {
    setIsLoadingPassword(true);
    try {
      const [_Response, _Error, _Code, _Errors] = await new AuthService().reset(
        data
      );
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => { },
          _Errors
        );
        setIsLoadingPassword(false);
        return false;
      }

      setError("");
      setIsLoadingPassword(false);
      return true;
    } catch (e) {
      setError("Houve um erro ao resetar senha.");
      setIsLoadingPassword(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      await new AuthService().logout();
      setUserLogged(null);
      localStorage.removeItem(`@beviAjudaGui:auth`);
      localStorage.removeItem(`@beviAjudaGui:user`);

      return { success: true, error: "" };
    } catch (err) {
      return { success: false, error: err };
    }
  };

  const onInvalid = (property?: any | undefined) => {
    return property ? "invalid" : "";
  };

  const isDevelopment = process.env.REACT_APP_ENV !== "production";

  const refreshProfileImage = async () => {
    const [response, error] = await profileService.me();
    if (error || !response) return;

    const imagePath =
      response?.data?.user?.user_profile_image ??
      response?.data?.user_profile_image ??
      null;

    setUserLogged((prev: any) => ({ ...prev, user_profile_image: imagePath ?? '' }));
  };

  const refreshProfileImageLocal = (imagePath: string) => {
    setUserLogged((prev: any) => ({ ...prev, user_profile_image: imagePath }));
  };

  return (
    <AuthContext.Provider
      value={{
        userLogged,
        setUserLogged,
        error,
        setError,
        isLoading,
        setIsLoading,
        accessToken,
        tokenLacqua,
        setTokenLacqua,
        login,
        authorization,
        logout,
        onInvalid,
        isLoadingLogged,
        origin,
        setOrigin,
        isDevelopment,
        forgot,
        reset,
        isLoadingPassword,
        isMainNavbar,
        setIsMainNavbar,
        refreshProfileImage,
        refreshProfileImageLocal,
        profileImageUrl,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);
